import React, { FC, useEffect, useState } from "react";

import { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useGetCurrencies,
  useGetUserPreferences,
  useUpdateUserPreferences,
} from "@api";

import { usePartnerInfo, withController } from "@utils";

import { Modal } from "@ui/components";
import { Button, Text, Title } from "@ui/elements";
import { Select } from "@ui/forms";
import { Stack } from "@ui/layout";

import { toaster } from "@components";

type ChangeCurrencyConfirmModalProps = {
  onClose: () => void;
  open: boolean;
  currency: string;
};

const ChangeCurrencyConfirmModal: FC<ChangeCurrencyConfirmModalProps> = ({
  onClose,
  open,
  currency,
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending, error } = useUpdateUserPreferences();

  const handleConfirm = async () => {
    await mutateAsync({ data: { currency, force: true } });
    toaster.success({
      title: t("messages.successful-update"),
      message: t("messages.currency-update-success"),
    });
    onClose();
  };

  const errorMessage = error?.response?.data.message;
  return (
    <Modal
      isActive={open}
      onClose={onClose}
      header={t("currency-mismatch")}
      style={{ zIndex: 100 }}
    >
      <Stack direction="column" gap="xl">
        <Stack direction="column" gap="xl">
          <Title size={6}>{t("currency-mismatch-intro")}</Title>
          <Stack direction="column">
            <Text
              dangerouslySetInnerHTML={{
                __html: t("currency-mismatch-warning", {
                  currency,
                }),
              }}
            />
            <Text>{t("messages.are-you-sure-continue")}</Text>
          </Stack>
        </Stack>
        {errorMessage && (
          <div className="notification is-danger is-light is-size-7">
            {errorMessage}
          </div>
        )}
        <Stack justify="end">
          <Button variant="light" onClick={onClose}>
            {t("labels.cancel")}
          </Button>
          <Button
            variant="light"
            color="danger"
            onClick={handleConfirm}
            isLoading={isPending}
          >
            {t("labels.im-sure")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

type CurrencyFormFields = {
  currency: string;
};

const ControlledSelect = withController<CurrencyFormFields>()(Select);

export const CurrencySettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currencyConfirmModalOpen, setCurrencyConfirmModalOpen] =
    useState(false);
  const { data: userPreferenceData } = useGetUserPreferences();
  const partnerInfo = usePartnerInfo();
  const { data: currenciesData, isLoading: currenciesLoading } =
    useGetCurrencies();
  const { handleSubmit, control, setValue, watch } =
    useForm<CurrencyFormFields>({
      defaultValues: {
        currency: userPreferenceData?.currency?.code,
      },
    });

  useEffect(() => {
    if (userPreferenceData) {
      setValue("currency", userPreferenceData?.currency?.code);
    }
  }, [userPreferenceData]);
  const { mutateAsync } = useUpdateUserPreferences();
  const currency = watch("currency");

  const onSubmit = async (values: CurrencyFormFields) => {
    setLoading(true);
    setError("");

    try {
      await mutateAsync({ data: values });

      toaster.success({
        title: t("messages.successful-update"),
        message: t("messages.currency-update-success"),
      });
    } catch (error) {
      setLoading(false);
      if (error instanceof AxiosError) {
        if (error.response?.data.message === "Currency Mismatch") {
          return setCurrencyConfirmModalOpen(true);
        }

        return setError(error.response?.data.message);
      }

      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ChangeCurrencyConfirmModal
        currency={currency}
        open={currencyConfirmModalOpen}
        onClose={() => setCurrencyConfirmModalOpen(false)}
      />
      <Stack direction="column" gap="xl">
        {error && (
          <div className="notification is-danger is-light">{error}</div>
        )}
        <Stack direction="column">
          <Text variant="secondary">{t("change-currency-info")}</Text>
        </Stack>

        <Stack direction="column" gap="xxs">
          <ControlledSelect
            isLoading={currenciesLoading}
            options={
              currenciesData?.map(({ symbol, name, code }) => ({
                label: t(`currencies.${name}`, { symbol }),
                value: code,
              })) || [
                {
                  label: t(`currenies.${userPreferenceData?.currency?.name}`, {
                    symbol: userPreferenceData?.currency?.symbol,
                  }),
                  value: userPreferenceData?.currency?.code || "",
                },
              ]
            }
            name="currency"
            control={control}
          />
          <Text variant="secondary" size="small">
            {t("not-found-yours")}{" "}
            <a
              target="_blank"
              href={`mailto:${partnerInfo.contact.email}`}
              rel="noreferrer"
            >
              {t("contact-us")}
            </a>
          </Text>
        </Stack>

        <Stack justify="end">
          <Button variant="light" isLoading={loading}>
            {t("labels.update")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
